import { FC, useState, useEffect, useMemo } from 'react';
import {
  useController,
  useFormContext,
  useFieldArray,
  Controller,
} from 'react-hook-form';
import {
  Grid,
  TextInput,
  ColorInput,
  Flex,
  BoxV2 as Box,
} from 'portal-commons';
import PhoneInput from './PhoneInput';
import { RcsCampaign } from '../../../types';
import CampaignAsset from '../../CampaignAsset';
import AssetSelectionModal from './AssetSelectionModal';
import { snakeToTitleCase } from '@/utils';
import { BrandAsset, BrandAssetType } from '@/apis/core';
import {
  createEmailValidator,
  createMaxLengthValidator,
  createWebsiteValidator,
  isRequired,
  isValidInput,
} from '@/applications/CspPortal/Campaigns/validators';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleX } from '@fortawesome/pro-regular-svg-icons';

interface CampaignDetailsFormProps {
  campaign: RcsCampaign;
  banner?: BrandAsset;
  logo?: BrandAsset;
  bannerOptions?: BrandAsset[];
  logoOptions?: BrandAsset[];
  disallowEdit?: boolean;
}

enum EditAssetType {
  None = 'none',
  Logo = 'logo',
  Banner = 'banner',
}

const MAX_FIELDS = 3;

interface CampaignDetailsFormValues {
  logo: BrandAsset;
  banner: BrandAsset;
  id: string;
  displayName: string;
  shortDescription: string;
  hostingRegion: string;
  billingCategory: string;
  usecase: string;
  websites: { value: string }[];
  websiteLabels: { value: string }[];
  phones: { value: string }[];
  phoneLabels: { value: string }[];
  emails: { value: string }[];
  emailLabels: { value: string }[];
  color: string;
  termsAndConditionsLink: string;
  privacyPolicyLink: string;
}

const CampaignDetailsForm: FC<CampaignDetailsFormProps> = ({
  campaign,
  banner: defaultBanner,
  logo: defaultLogo,
  bannerOptions = [],
  logoOptions = [],
  disallowEdit = false,
}) => {
  const { control, clearErrors, reset } =
    useFormContext<CampaignDetailsFormValues>();
  const [editAssetType, setEditAssetType] = useState<EditAssetType>(
    EditAssetType.None
  );

  const handleSelectAsset = (asset: BrandAsset) => {
    if (editAssetType === EditAssetType.Banner) {
      setBanner(asset);
    } else {
      setLogo(asset);
    }
    setEditAssetType(EditAssetType.None);
  };

  const defaultValues = useMemo(() => {
    return {
      logo: defaultLogo,
      banner: defaultBanner,
      id: campaign.uid || '',
      displayName: campaign.displayName || '',
      shortDescription: campaign.shortDescription || '',
      hostingRegion: campaign.hostingRegion || '',
      billingCategory: campaign.billingCategory || '',
      usecase: campaign.usecase || '',
      websites: (campaign.websites || []).map((v) => ({ value: v })),
      websiteLabels: (campaign.websiteLabels || []).map((v) => ({
        value: v,
      })),
      phones: (campaign.phones || []).map((v) => ({ value: v })),
      phoneLabels: (campaign.phoneLabels || []).map((v) => ({ value: v })),
      emails: (campaign.emails || []).map((v) => ({ value: v })),
      emailLabels: (campaign.emailLabels || []).map((v) => ({ value: v })),
      color: campaign.color || '3A49E9',
      termsAndConditionsLink: campaign.termsAndConditionsLink || '',
      privacyPolicyLink: campaign.privacyPolicyLink || '',
    };
  }, [campaign, defaultLogo, defaultBanner]);

  const {
    field: { value: logo, onChange: setLogo },
  } = useController({ control, name: 'logo', defaultValue: defaultLogo });

  const {
    field: { value: banner, onChange: setBanner },
  } = useController({ control, name: 'banner', defaultValue: defaultBanner });

  // ReadOnly fields
  const {
    field: { value: id },
  } = useController({
    control,
    name: 'id',
    defaultValue: campaign.uid || '',
  });

  const {
    field: { value: displayName },
  } = useController({
    control,
    name: 'displayName',
    defaultValue: campaign.displayName || '',
  });

  const {
    field: { value: color, onChange: setColor },
  } = useController({
    control,
    name: 'color',
    defaultValue: campaign.color || '3A49E9',
  });

  const {
    field: { value: usecase },
  } = useController({
    control,
    name: 'usecase',
    defaultValue: campaign.usecase || '',
  });

  const {
    field: { value: hostingRegion },
  } = useController({
    control,
    name: 'hostingRegion',
    defaultValue: campaign.hostingRegion || '',
  });
  const {
    field: { value: billingCategory },
  } = useController({
    control,
    name: 'billingCategory',
    defaultValue: campaign.billingCategory || '',
  });

  // Editable fields
  const {
    field: { value: shortDescription, onChange: setShortDescription },
    fieldState: { error: shortDescriptionError },
  } = useController({
    control,
    name: 'shortDescription',
    defaultValue: campaign.shortDescription || '',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: {
      value: termsAndConditionsLink,
      onChange: setTermsAndConditionsLink,
    },
    fieldState: { error: termsAndConditionsLinkError },
  } = useController({
    control,
    name: 'termsAndConditionsLink',
    defaultValue: campaign.termsAndConditionsLink || '',
    rules: {
      validate: {
        isRequired,
        withValidInput: createWebsiteValidator(),
      },
    },
  });

  const {
    field: { value: privacyPolicyLink, onChange: setPrivacyPolicyLink },
    fieldState: { error: privacyPolicyLinkError },
  } = useController({
    control,
    name: 'privacyPolicyLink',
    defaultValue: campaign.privacyPolicyLink || '',
    rules: {
      validate: {
        isRequired,
        withValidInput: createWebsiteValidator(),
      },
    },
  });

  const {
    fields: websiteFields,
    remove: removeWebsite,
    append: addWebsite,
  } = useFieldArray({
    control,
    name: 'websites',
  });
  const {
    fields: websiteLabelFields,
    remove: removeWebsiteLabel,
    append: addWebsiteLabel,
  } = useFieldArray({
    control,
    name: 'websiteLabels',
  });

  const {
    fields: phoneFields,
    remove: removePhone,
    append: addPhone,
  } = useFieldArray({ control, name: 'phones' });
  const {
    fields: phoneLabelFields,
    remove: removePhoneLabel,
    append: addPhoneLabel,
  } = useFieldArray({
    control,
    name: 'phoneLabels',
  });

  const {
    fields: emailFields,
    remove: removeEmail,
    append: addEmail,
  } = useFieldArray({ control, name: 'emails' });
  const {
    fields: emailLabelFields,
    remove: removeEmailLabel,
    append: addEmailLabel,
  } = useFieldArray({
    control,
    name: 'emailLabels',
  });

  useEffect(() => {
    clearErrors();
    reset(defaultValues);
  }, [defaultValues]);

  const handleEditAsset = (type: BrandAssetType) => {
    if (type === BrandAssetType.Logo) {
      setEditAssetType(EditAssetType.Logo);
    } else {
      setEditAssetType(EditAssetType.Banner);
    }
  };

  const isLastItem = (index: number, fields: { value: string }[]) => {
    return fields.length < MAX_FIELDS && index === fields.length - 1;
  };

  return (
    <>
      <Grid
        sx={{
          gridTemplateColumns: '1fr 1fr',
          columnGap: 'l',
          rowGap: 'l',
          mb: 'l',
        }}
      >
        <CampaignAsset
          type={BrandAssetType.Logo}
          name={logo?.name || ''}
          uuid={logo?.attachmentUuid || ''}
          action={disallowEdit ? null : 'edit'}
          onEdit={() => handleEditAsset(BrandAssetType.Logo)}
          dimension={{ width: '127px', height: '127px' }}
        />
        <CampaignAsset
          type={BrandAssetType.Banner}
          name={banner?.name || ''}
          uuid={banner?.attachmentUuid || ''}
          action={disallowEdit ? null : 'edit'}
          onEdit={() => handleEditAsset(BrandAssetType.Banner)}
          dimension={{ width: '379px', height: '127px' }}
        />

        <Flex sx={{ flexDirection: 'column' }}>
          <TextInput
            label="ID"
            value={id}
            readOnly
            style={{ marginBottom: 18 }}
          />
          <TextInput label="Display Name" value={displayName} readOnly />
        </Flex>
        <TextInput
          label="Short Description"
          multiline
          minRows={3}
          maxRows={3}
          value={shortDescription}
          onChange={setShortDescription}
          error={!!shortDescriptionError?.message}
          note={shortDescriptionError?.message}
          data-testid="shortDescription"
        />

        <ColorInput
          value={`#${color}`}
          onChange={(val) => {
            setColor(val.replace('#', ''));
          }}
          data-testid="color"
        />
        <TextInput
          label="Use Case"
          value={snakeToTitleCase(usecase)}
          readOnly
        />

        <TextInput
          label="Hosting Region"
          value={snakeToTitleCase(hostingRegion)}
          readOnly
        />
        <TextInput
          label="Terms of Service URL"
          value={termsAndConditionsLink}
          onChange={setTermsAndConditionsLink}
          error={!!termsAndConditionsLinkError?.message}
          note={termsAndConditionsLinkError?.message}
          data-testid="termsAndConditionsLink"
        />

        <TextInput
          label="Billing Category"
          value={snakeToTitleCase(billingCategory)}
          readOnly
        />
        <TextInput
          label="Privacy Policy URL"
          value={privacyPolicyLink}
          onChange={setPrivacyPolicyLink}
          error={!!privacyPolicyLinkError?.message}
          note={privacyPolicyLinkError?.message}
          data-testid="privacyPolicyLink"
        />
        <div style={{ gridColumn: '1 / -1', height: '20px' }} />

        {websiteFields.map((field, index) => (
          <>
            <Flex
              sx={{
                position: 'relative',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                height: 'fit-content',
              }}
            >
              {index > 0 && (
                <DeleteIcon
                  onClick={() => {
                    removeWebsite(index);
                    removeWebsiteLabel(index);
                  }}
                />
              )}
              <Controller
                key={field.id}
                control={control}
                name={`websites.${index}.value`}
                rules={{
                  validate: {
                    isRequired,
                    withValidInput: createWebsiteValidator(),
                  },
                }}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => (
                  <TextInput
                    style={{ flexGrow: 1 }}
                    label={`${index === 0 ? 'Primary ' : ''}Website`}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    note={error?.message}
                    data-testid={`websites[${index}]`}
                  />
                )}
              />
              {isLastItem(index, websiteFields) && (
                <AddFieldBtn
                  btnText="Add Website"
                  onClick={() => {
                    addWebsite({ value: '' });
                    addWebsiteLabel({ value: '' });
                  }}
                />
              )}
            </Flex>
            <Controller
              key={websiteLabelFields[index].id}
              control={control}
              name={`websiteLabels.${index}.value`}
              rules={{
                validate: {
                  isRequired,
                  withValidMaxLength: createMaxLengthValidator(25),
                },
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => (
                <TextInput
                  label={`Label for ${index === 0 ? 'Primary ' : ''}Website`}
                  style={{
                    marginBottom:
                      index === websiteFields.length - 1 ? '20px' : '0',
                  }}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error?.message}
                  note={error?.message}
                  data-testid={`websiteLabels[${index}]`}
                />
              )}
            />
          </>
        ))}
        {phoneFields.map((field, index) => (
          <>
            <Flex
              sx={{
                position: 'relative',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                height: 'fit-content',
              }}
            >
              {index > 0 && (
                <DeleteIcon
                  onClick={() => {
                    removePhone(index);
                    removePhoneLabel(index);
                  }}
                />
              )}
              <Controller
                key={field.id}
                control={control}
                name={`phones.${index}.value`}
                rules={{
                  validate: {
                    isRequired,
                    isValidInput,
                    withValidMaxLength: createMaxLengthValidator(20),
                  },
                }}
                render={({
                  field: { onBlur, onChange, value },
                  fieldState: { error },
                }) => (
                  <PhoneInput
                    error={!!error?.message}
                    note={error?.message}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={`${index === 0 ? 'Primary ' : ''}Phone Number`}
                    data-testid={`phones[${index}]`}
                  />
                )}
              />
              {isLastItem(index, phoneFields) && (
                <AddFieldBtn
                  btnText="Add Phone Number"
                  onClick={() => {
                    addPhone({ value: '' });
                    addPhoneLabel({ value: '' });
                  }}
                />
              )}
            </Flex>
            <Controller
              key={phoneLabelFields[index].id}
              control={control}
              name={`phoneLabels.${index}.value`}
              rules={{
                validate: {
                  isRequired,
                  withValidMaxLength: createMaxLengthValidator(25),
                },
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => (
                <TextInput
                  label={`Label for ${
                    index === 0 ? 'Primary ' : ''
                  }Phone Number`}
                  style={{
                    marginBottom:
                      index === phoneFields.length - 1 ? '20px' : '0',
                  }}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error?.message}
                  note={error?.message}
                  data-testid={`phoneLabels[${index}]`}
                />
              )}
            />
          </>
        ))}
        {emailFields.map((field, index) => (
          <>
            <Flex
              sx={{
                position: 'relative',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                height: 'fit-content',
              }}
            >
              {index > 0 && (
                <DeleteIcon
                  onClick={() => {
                    removeEmail(index);
                    removeEmailLabel(index);
                  }}
                />
              )}
              <Controller
                key={field.id}
                control={control}
                name={`emails.${index}.value`}
                rules={{
                  validate: {
                    isRequired,
                    withValidInput: createEmailValidator(),
                  },
                }}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => (
                  <TextInput
                    style={{ flexGrow: 1 }}
                    label={`${index === 0 ? 'Primary ' : ''}Email ID`}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    note={error?.message}
                    data-testid={`emails[${index}]`}
                  />
                )}
              />
              {isLastItem(index, emailFields) && (
                <AddFieldBtn
                  btnText="Add Email ID"
                  onClick={() => {
                    addEmail({ value: '' });
                    addEmailLabel({ value: '' });
                  }}
                />
              )}
            </Flex>
            <Controller
              key={emailLabelFields[index].id}
              control={control}
              name={`emailLabels.${index}.value`}
              rules={{
                validate: {
                  isRequired,
                  withValidMaxLength: createMaxLengthValidator(25),
                },
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => (
                <TextInput
                  label={`Label for ${index === 0 ? 'Primary ' : ''}Email ID`}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error?.message}
                  note={error?.message}
                  data-testid={`emailLabels[${index}]`}
                />
              )}
            />
          </>
        ))}
      </Grid>
      {editAssetType !== EditAssetType.None && (
        <AssetSelectionModal
          type={
            editAssetType === EditAssetType.Banner
              ? BrandAssetType.Banner
              : BrandAssetType.Logo
          }
          open={true}
          onClose={() => setEditAssetType(EditAssetType.None)}
          options={
            editAssetType === EditAssetType.Banner ? bannerOptions : logoOptions
          }
          onSave={handleSelectAsset}
          brandUid={campaign.brandUid}
          defaultValue={
            editAssetType === EditAssetType.Banner
              ? banner?.attachmentUuid
              : logo?.attachmentUuid
          }
        />
      )}
    </>
  );
};

export const DeleteIcon: FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <Box
      sx={{
        alignSelf: 'flex-start',
        marginTop: 10,
        color: 't.black60',
        fontWeight: 400,
        fontSize: 'H500',
        lineHeight: 'normal',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faCircleX} />
    </Box>
  );
};

export const AddFieldBtn: FC<{
  btnText: string;
  onClick: () => void;
}> = ({ btnText, onClick }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '44px',
        right: 0,
        fontSize: 'H200',
        fontWeight: 600,
        color: '#33A7C2',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {btnText}
    </Box>
  );
};

export default CampaignDetailsForm;
